import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { Tenant } from '../../services/tenant.service';
import { TenantService } from '../../services/tenant.service';
import { PlatformService } from '../../core/platform/platform.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-login',
    imports: [ReactiveFormsModule, RouterModule, TranslateModule],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  readonly appVersion = environment.appVersion;
  loginForm!: FormGroup;
  isLoading = false;
  error: string | null = null;
  successMessage: string | null = null;
  devMagicLink: string | null = null;
  devNote: string | null = null;
  isMobile = false;
  showTenantSelect = false;
  availableTenants: Tenant[] = [];

  private destroy$ = new Subject<void>();
  private readonly onResize = (): void => this.checkMobileView();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private tenantService: TenantService,
    private router: Router,
    private platformService: PlatformService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.checkMobileView();
    this.detectTenant();
    
    // Only attach resize listener in browser
    if (this.platformService.isBrowser()) {
      this.platformService.addWindowEventListener('resize', this.onResize);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Only remove resize listener in browser
    if (this.platformService.isBrowser()) {
      this.platformService.removeWindowEventListener('resize', this.onResize);
    }
  }

  /**
   * Initialize reactive form
   */
  private initializeForm(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      tenant: [''] // Optional for public sites
    });
  }

  /**
   * Check if mobile view (SSR-safe)
   */
  private checkMobileView(): void {
    if (this.platformService.isBrowser()) {
      this.isMobile = this.platformService.getViewportWidth() < 768;
    } else {
      this.isMobile = false; // Default to desktop view during SSR
    }
  }

  /**
   * Detect tenant from URL/domain and populate form
   */
  private detectTenant(): void {
    if (!this.platformService.isBrowser()) {
      this.showTenantSelect = true;
      return;
    }

    const tenantFromHost = this.tenantService.extractTenantFromHost(
      this.platformService.getLocationHostname()
    );

    if (tenantFromHost) {
      this.showTenantSelect = false;
      this.loginForm.patchValue({ tenant: tenantFromHost });
      this.tenantService.setCurrentTenantSchema(tenantFromHost);
      return;
    }

    this.tenantService.detectCurrentTenant()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ tenant }) => {
          if (tenant && tenant.schema_name) {
            // We're on a tenant-specific domain - hide selector and pre-fill form
            this.showTenantSelect = false;
            this.loginForm.patchValue({ tenant: tenant.schema_name });
            return;
          }

          // We're on a public/multi-tenant domain - show tenant selector
          this.showTenantSelect = true;
          this.loadAvailableTenants();
        },
        error: () => {
          // Default to showing tenant selector if detection fails
          this.showTenantSelect = true;
          this.loadAvailableTenants();
        }
      });
  }

  /**
   * Load available tenants for public login page
   */
  private loadAvailableTenants(): void {
    this.authService.getAvailableTenants()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tenants: Tenant[]) => {
          this.availableTenants = tenants;
        },
        error: (error) => {
          console.error('Failed to load available tenants', error);
          this.availableTenants = [];
        }
      });
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;

    if (isLoading) {
      this.loginForm.disable({ emitEvent: false });
      return;
    }

    this.loginForm.enable({ emitEvent: false });
  }

  /**
   * Form getters
   */
  get email() {
    return this.loginForm.get('email');
  }

  get tenant() {
    return this.loginForm.get('tenant');
  }

  /**
   * Handle form submission - send magic link
   */
  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.setLoadingState(true);
    this.error = null;
    this.successMessage = null;
    this.devMagicLink = null;
    this.devNote = null;

    const emailValue = this.email?.value;
    const tenantValue = this.tenant?.value;

    this.authService.startMagicLink(emailValue, tenantValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.setLoadingState(false);
          this.successMessage = 'Link logowania został wysłany na Twój email. Sprawdź skrzynkę!';
          this.devMagicLink = response.dev_magic_link ?? null;
          this.devNote = response.dev_note ?? null;
          
          // Reset form after success
          this.loginForm.reset();

          // Optionally redirect after delay
          setTimeout(() => {
            // Could redirect to confirmation page or back to landing
            // this.router.navigate(['/confirm']);
          }, 3000);
        },
        error: (error) => {
          this.setLoadingState(false);
          this.error = this.getErrorMessage(error);
        }
      });
  }

  /**
   * Extract user-friendly error message
   */
  private getErrorMessage(error: any): string {
    if (error?.error?.detail) {
      return error.error.detail;
    }
    
    if (error?.error?.email && Array.isArray(error.error.email)) {
      return error.error.email[0];
    }

    if (error?.status === 429) {
      return 'auth.errors.rate_limit';  // Translation key
    }

    if (error?.status === 400) {
      return 'auth.login.errors.invalid';  // Translation key
    }

    if (error?.status === 0) {
      return 'common.errors.network';  // Translation key
    }

    return 'auth.login.errors.unknown';  // Translation key
  }
}
